import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Flex, Button } from "antd"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { ConfirmModal } from "ui/ConfirmModal"
import { spacings } from "themes/variables"
import UploadArea from "./components/UploadArea"
import FilesList from "./components/FilesList"
import styles from "./index.module.css"

/**
 * Unified LicensesModal component for adding and editing licenses and certifications
 */
const LicensesModal = ({ open, onCancel, onSave, mode = "add", initialFiles = [] }) => {
  const { t } = useTranslation()

  // Initialize files based on mode
  const getInitialFiles = () => {
    if (mode === "edit" && initialFiles) {
      return initialFiles.map((file, index) => ({
        uid: file.uid || `${index}`,
        name: file.name,
        status: file.status || "done",
        percent: file.percent || 100
      }))
    }
    return []
  }

  const [fileList, setFileList] = useState(getInitialFiles())
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(null)

  // Update files when modal opens with new data
  useEffect(() => {
    if (open) {
      setFileList(getInitialFiles())
    }
  }, [open, mode, initialFiles])

  const handleBeforeUpload = (file) => {
    const isValidType = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "image/jpeg",
      "image/jpg"
    ].includes(file.type)

    const isValidSize = file.size / 1024 / 1024 < 5

    if (!isValidType) {
      console.error("You can only upload DOC, DOCX, PDF, TXT, JPG files!")
      return false
    }

    if (!isValidSize) {
      console.error("File must be smaller than 5MB!")
      return false
    }

    return true
  }

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList)
  }

  const handleDeleteClick = (file) => {
    setFileToDelete(file)
    setIsDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!fileToDelete) return

    const updatedFiles = fileList.filter((file) => file.uid !== fileToDelete.uid)
    setFileList(updatedFiles)
    setIsDeleteConfirmOpen(false)
    setFileToDelete(null)
  }

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false)
    setFileToDelete(null)
  }

  const handleSaveChanges = () => {
    // TODO: Process files and call onSave with appropriate data
    console.log(`${mode === "edit" ? "Update" : "Save"} licenses`, fileList)
    onSave(fileList)
  }

  const handleCancel = () => {
    if (mode === "add") {
      // Reset to initial blank state for add mode
      setFileList([])
    } else {
      // Revert to original data for edit mode
      setFileList(getInitialFiles())
    }
    onCancel()
  }

  const modalTitle = mode === "edit" ? t("crewMember.licenses.editTitle") : t("crewMember.licenses.addTitle")

  const isSaveDisabled = fileList.length === 0

  return (
    <>
      <Modal open={open} onCancel={handleCancel} title={modalTitle} width={800} className={styles.modal} centered footer={null}>
        <Flex vertical gap={spacings.XL} className={styles.modalContent}>
          {/* Description */}
          <p className={styles.description}>{t("crewMember.licenses.description")}</p>

          {/* Upload Section */}
          <Flex vertical gap={spacings.M}>
            <UploadArea fileList={fileList} onBeforeUpload={handleBeforeUpload} onChange={handleChange} />

            <FilesList files={fileList} onDeleteClick={handleDeleteClick} />
          </Flex>

          {/* Footer Buttons */}
          <Flex gap={spacings.S} justify="flex-start" className={styles.modalFooter}>
            <Button onClick={handleCancel} variant="outlined" color="primary" size="large" className={styles.cancelButton}>
              {t("crewMember.licenses.buttons.cancel")}
            </Button>
            <Button type="primary" onClick={handleSaveChanges} size="large" disabled={isSaveDisabled} className={styles.saveButton}>
              {mode === "edit" ? t("crewMember.licenses.buttons.saveChanges") : t("crewMember.licenses.buttons.save")}
            </Button>
          </Flex>
        </Flex>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={isDeleteConfirmOpen}
        title={t("crewMember.licenses.deleteConfirmation.title")}
        info={t("crewMember.licenses.deleteConfirmation.message", {
          fileName: fileToDelete?.name || ""
        })}
        onCancel={handleCancelDelete}
        onDelete={handleConfirmDelete}
        cancelLabel={t("crewMember.licenses.buttons.cancel")}
        deleteLabel={t("crewMember.licenses.buttons.delete")}
      />
    </>
  )
}

LicensesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["add", "edit"]),
  initialFiles: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string,
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      percent: PropTypes.number
    })
  )
}

export default LicensesModal
